import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ImageGallery from '../../Common/Images/ImageGallery.jsx';
import ProjectsGallery from '../../ProjectsAndSkills/Projects/ProjectsGallery.jsx';
import SkillsGallery from '../../ProjectsAndSkills/Skills/SkillsGallery.jsx';
import SideMenu from '../../Common/Menus/SideMenu.jsx';
import { getImage, getSharedImage } from '../../Common/Images/utilities/imageUtils.js';
import { createHtmlElementTemplate } from '../utilities/htmlElementTemplates.js';
import { createHydratedCollection, getCollectionMenuAvailability } from '../utilities/htmlCollectionUtils.js';
import { SAFE_TAGS } from '../utilities/htmlEditorUtils.js';
import { EDITOR_MODES } from '../utilities/simpleEditorMode.js';
import { SKILLS_ICON_CLASS } from '../utilities/editorUiUtils.js';
import { searchAddContentItems } from '../utilities/addContentSearch.js';
import {
  createConfiguredAddContentNode,
  getAddContentCategories,
  getConfiguredAddContentItems,
} from '../utilities/addContentConfig.js';
import useEscapeToClose from '../utilities/useEscapeToClose.js';

const DISALLOWED_ITEM_ALIASES = {
  a: 'link',
  img: 'image',
};

const AddItemButton = ({
  label,
  detail,
  iconClass,
  onClick,
  variant = 'dark',
  disabled = false,
  category = '',
}) => (
  <button
    className={`btn btn-outline-${variant} add-menu-item-button`}
    onClick={onClick}
    disabled={disabled}
    type="button"
  >
    <i className={`bi ${iconClass} add-menu-item-icon`} aria-hidden="true"></i>
    <span className="add-menu-item-copy">
      <span className="add-menu-item-name">{label}</span>
      <span className="add-menu-item-detail">{detail}</span>
      {category ? <span className="add-menu-item-category">{category}</span> : null}
    </span>
  </button>
);

AddItemButton.propTypes = {
  label: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  category: PropTypes.string,
};

const AddItemsMenu = ({
  onAddItem,
  setShowAddMenu,
  showAddMenu,
  userId,
  disallowedItems = [],
  requestConfirmation,
  editorMode = EDITOR_MODES.ADVANCED,
  aiEnabled = false,
  onOpenAISection,
}) => {
  const [showGallery, setShowGallery] = useState(false);
  const [showProjectsGallery, setShowProjectsGallery] = useState(false);
  const [showSkillsGallery, setShowSkillsGallery] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [collectionBusy, setCollectionBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [customTag, setCustomTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const simpleMode = editorMode === EDITOR_MODES.SIMPLE;

  const blockedItems = new Set(disallowedItems.map((item) => String(item || '').trim().toLowerCase()));
  const isItemAllowed = (itemName) => {
    const normalizedName = String(itemName || '').toLowerCase();
    return !blockedItems.has(normalizedName) && !blockedItems.has(DISALLOWED_ITEM_ALIASES[normalizedName]);
  };

  const addElement = (itemJSON, insertAtIndex = null) => {
    onAddItem(itemJSON, insertAtIndex);
    setShowAddMenu(false);
  };

  const addGalleryImage = async (imageJSON) => {
    setMenuError('');

    try {
      const imageData = userId
        ? await getImage(userId, imageJSON.imageId)
        : await getSharedImage(imageJSON.imageId);
      const imageNode = createHtmlElementTemplate('img');

      imageNode.attributes = {
        ...imageNode.attributes,
        src: imageData?.base64 || '',
        alt: imageData?.alt || imageData?.filename || 'Image',
        'data-image-id': imageJSON.imageId,
      };

      addElement(imageNode);
    } catch (error) {
      console.error('Failed to load selected image:', error);
      setMenuError(error.message || 'Failed to load selected image.');
    } finally {
      setShowGallery(false);
    }
  };

  const {
    projects: projectsAllowed,
    skills: skillsAllowed,
    dataTab: dataTabAllowed,
  } = getCollectionMenuAvailability(disallowedItems, userId);
  const availableCategories = getAddContentCategories(editorMode)
    .filter(({ id }) => id !== 'data' || dataTabAllowed);
  const defaultTab = availableCategories[0]?.id || (simpleMode ? 'sections' : 'basic');

  useEffect(() => {
    if (activeTab === 'data' && !dataTabAllowed) {
      setActiveTab(defaultTab);
    }
  }, [activeTab, dataTabAllowed, defaultTab]);

  useEffect(() => {
    setActiveTab(defaultTab);
    setSearchQuery('');
  }, [defaultTab, simpleMode]);

  useEffect(() => {
    if (!showAddMenu) setSearchQuery('');
  }, [showAddMenu]);

  useEscapeToClose(
    showAddMenu || showGallery || showProjectsGallery || showSkillsGallery,
    () => {
      if (showGallery) setShowGallery(false);
      else if (showProjectsGallery) setShowProjectsGallery(false);
      else if (showSkillsGallery) setShowSkillsGallery(false);
      else setShowAddMenu(false);
    }
  );

  const addCustomElement = () => {
    const tagName = customTag.trim().toLowerCase();
    if (!SAFE_TAGS.has(tagName) || !isItemAllowed(tagName) || tagName === 'projects' || tagName === 'skills') {
      setMenuError('That item type is not available. Add projects and skills from the Portfolio tab.');
      return;
    }

    setMenuError('');
    addElement(createHtmlElementTemplate(tagName));
    setCustomTag('');
  };

  const addCollection = async (kind, recordIds) => {
    setCollectionBusy(true);
    setMenuError('');
    try {
      const collectionNode = await createHydratedCollection(kind, recordIds, userId);
      addElement(collectionNode);
    } catch (error) {
      console.error(`Failed to add ${kind} collection:`, error);
      setMenuError(error.message || `Failed to add ${kind} collection.`);
    } finally {
      setCollectionBusy(false);
      setShowProjectsGallery(false);
      setShowSkillsGallery(false);
    }
  };

  const categoryById = new Map(availableCategories.map((category) => [category.id, category]));
  const configuredItems = getConfiguredAddContentItems(editorMode, {
    isTagAllowed: isItemAllowed,
  });
  const configuredMenuItems = configuredItems.map((item) => ({
    ...item,
    key: item.id,
    category: categoryById.get(item.category)?.label || item.category,
    categoryId: item.category,
    onClick: () => {
      try {
        addElement(createConfiguredAddContentNode(item));
      } catch (error) {
        setMenuError(error.message || 'That content could not be added.');
      }
    },
  }));

  // These entries launch data-aware workflows, so their behavior stays in React.
  const specialMenuItems = [
    ...(isItemAllowed('img') ? [{
      key: 'special-image',
      label: 'Image',
      detail: 'Choose from your saved images',
      iconClass: 'bi-image',
      category: simpleMode ? 'Content' : 'Elements',
      categoryId: simpleMode ? 'content' : 'basic',
      keywords: ['photo', 'picture', 'media'],
      onClick: () => setShowGallery(true),
    }] : []),
    ...(projectsAllowed ? [{
      key: 'special-projects',
      label: 'Projects',
      detail: simpleMode ? 'Show work from your saved projects' : 'Uses your saved projects',
      iconClass: 'bi-kanban',
      category: 'Portfolio',
      categoryId: 'data',
      keywords: ['work', 'case studies'],
      disabled: collectionBusy,
      onClick: () => setShowProjectsGallery(true),
    }] : []),
    ...(skillsAllowed ? [{
      key: 'special-skills',
      label: 'Skills',
      detail: simpleMode ? 'Show your saved skills and ratings' : 'Uses your saved skills',
      iconClass: SKILLS_ICON_CLASS,
      category: 'Portfolio',
      categoryId: 'data',
      keywords: ['abilities', 'strengths', 'rating'],
      disabled: collectionBusy,
      onClick: () => setShowSkillsGallery(true),
    }] : []),
    ...(!simpleMode ? [{
      key: 'special-custom',
      label: 'Custom Element',
      detail: 'Enter an allowed HTML item type',
      iconClass: 'bi-braces',
      category: 'Advanced',
      categoryId: 'custom',
      keywords: ['html', 'tag', 'custom'],
      onClick: () => {
        setActiveTab('custom');
        setSearchQuery('');
      },
    }] : []),
  ];
  const allMenuItems = [...configuredMenuItems, ...specialMenuItems];
  const activeCategory = categoryById.get(activeTab);
  const activeItems = allMenuItems.filter((item) => item.categoryId === activeTab);
  const visibleActiveItems = simpleMode && activeTab === 'sections'
    ? [
      ...activeItems,
      ...specialMenuItems.filter((item) => item.categoryId === 'data'),
    ]
    : activeItems;

  const searchActive = Boolean(searchQuery.trim());
  const searchResults = searchAddContentItems(allMenuItems, searchQuery);
  const renderMenuItem = (item, showCategory = false) => (
    <AddItemButton
      key={item.key}
      label={item.label}
      detail={item.detail}
      iconClass={item.iconClass}
      category={showCategory ? item.category : ''}
      variant={item.variant}
      disabled={item.disabled}
      onClick={item.onClick}
    />
  );

  return (
    <SideMenu
      className="new-blog-side-menu"
      animate
      side="left"
      title="Add Content"
      iconClass="bi-file-earmark-plus"
      expanded={showAddMenu}
      setExpanded={setShowAddMenu}
    >
      {menuError ? <div className="alert alert-danger small">{menuError}</div> : null}

      <div className="add-menu-search">
        <div className="input-group input-group-sm">
          <span className="input-group-text" aria-hidden="true">
            <i className="bi bi-search"></i>
          </span>
          <input
            className="form-control"
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search content"
            aria-label="Search content"
            autoComplete="off"
          />
          {searchActive ? (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <i className="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          ) : null}
        </div>
      </div>

      {!searchActive && aiEnabled ? (
        <div className="add-menu-ai mb-3">
          <button
            className="btn btn-outline-primary btn-sm w-100"
            type="button"
            onClick={() => {
              setShowAddMenu(false);
              onOpenAISection?.();
            }}
          >
            <i className="bi bi-stars me-2" aria-hidden="true"></i>
            Create a section with AI
          </button>
        </div>
      ) : null}

      {searchActive ? (
        <section className="add-menu-search-results" aria-live="polite">
          <div className="add-menu-search-summary">
            <strong>Search results</strong>
            <span>{searchResults.length}</span>
          </div>
          {searchResults.length ? (
            <div className="add-menu-item-grid">
              {searchResults.map((item) => renderMenuItem(item, true))}
            </div>
          ) : (
            <div className="add-menu-empty-search">
              <i className="bi bi-search" aria-hidden="true"></i>
              <strong>No matching content</strong>
              <span>Try a simpler word, such as image, heading, or projects.</span>
              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setSearchQuery('')}>
                Clear search
              </button>
            </div>
          )}
        </section>
      ) : null}

      {!searchActive ? <div className="add-menu-tabs mb-3" role="tablist" aria-label="Add content tabs">
        {availableCategories.map(({ id, label, iconClass }) => (
            <button
              key={id}
              className={`btn btn-sm ${activeTab === id ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab(id)}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
            >
              <i className={`bi ${iconClass} me-1`} aria-hidden="true"></i>{label}
            </button>
          ))}
      </div> : null}

      {!searchActive && activeCategory?.groups ? (
        <div className="add-menu-section-stack">
          {activeCategory.groups.map((group) => (
            <section key={group.id}>
              <h6 className="add-menu-section-title">{group.label}</h6>
              <div className="add-menu-item-grid">
                {visibleActiveItems
                  .filter((item) => item.group === group.id)
                  .map((item) => renderMenuItem(item))}
              </div>
            </section>
          ))}
        </div>
      ) : null}

      {!searchActive && !activeCategory?.groups && activeTab !== 'custom' ? (
        <div className="add-menu-item-grid">
          {visibleActiveItems.map((item) => renderMenuItem(item))}
        </div>
      ) : null}

      {!searchActive && !simpleMode && activeTab === 'custom' ? (
        <div>
          <label className="form-label small fw-semibold">Item type</label>
          <div className="input-group input-group-sm">
            <input
              className="form-control"
              placeholder="section"
              value={customTag}
              onChange={(event) => {
                setCustomTag(event.target.value);
                setMenuError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addCustomElement();
                }
              }}
            />
            <button
              className="btn btn-outline-primary"
              onClick={addCustomElement}
              type="button"
            >
              Add
            </button>
          </div>
        </div>
      ) : null}

      {showGallery ? (
        <ImageGallery
          userId={userId}
          setShowGallery={setShowGallery}
          addElement={addGalleryImage}
          requestConfirmation={requestConfirmation}
        />
      ) : null}

      {showProjectsGallery ? (
        <ProjectsGallery
          userId={userId}
          onSelect={(projectIds) => void addCollection('project', projectIds)}
          onClose={() => setShowProjectsGallery(false)}
          multiSelect
        />
      ) : null}

      {showSkillsGallery ? (
        <div className="new-blog-skills-selection">
          <SkillsGallery
            userId={userId}
            onSelect={(skillIds) => void addCollection('skill', skillIds)}
            setExpanded={setShowSkillsGallery}
            multiSelect
          />
        </div>
      ) : null}
    </SideMenu>
  );
};

AddItemsMenu.propTypes = {
  onAddItem: PropTypes.func.isRequired,
  setShowAddMenu: PropTypes.func.isRequired,
  showAddMenu: PropTypes.bool,
  userId: PropTypes.string,
  disallowedItems: PropTypes.arrayOf(PropTypes.string),
  requestConfirmation: PropTypes.func,
  editorMode: PropTypes.oneOf(Object.values(EDITOR_MODES)),
  aiEnabled: PropTypes.bool,
  onOpenAISection: PropTypes.func,
};

export default AddItemsMenu;
